import { Inject, Injectable } from '@angular/core';
import {
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQuery,
  DwQueryFilterGroup,
  DwQueryHelper
} from '@devwareapps/devware-cap';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AdminManagerService } from '../../../shared/services/admin-manager.service';
import {
  ReportFilterPresetOwnershipType,
  ReportFilterPresetManageResult,
  ReportFilterPreferenceRecord,
  ReportFilterPresetRecord,
  ReportSavedFilterState,
  ResolvedReportFilterPreset
} from '../models/report-filter-preset.model';
import { ReportFilterPresetCodecService } from './report-filter-preset-codec.service';

const REPORT_FILTER_PRESET_ITEM = 'ReportFilterPreset';
const REPORT_FILTER_PREFERENCE_ITEM = 'ReportFilterPreference';

@Injectable({ providedIn: 'root' })
export class ReportFilterPresetService {
  constructor(
    @Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService,
    private adminManager: AdminManagerService,
    private codec: ReportFilterPresetCodecService
  ) { }

  loadPresets(dashboardKey: string): Observable<ReportFilterPresetRecord[]> {
    const query = new DwQuery(REPORT_FILTER_PRESET_ITEM);
    const filters = new DwQueryHelper().filters;
    query.SetFieldSettings(false, true);
    query.AddFilterAnd(filters.Equal(`${REPORT_FILTER_PRESET_ITEM}.DashboardKey`, dashboardKey));
    query.AddFilterAnd(filters.Equal(`${REPORT_FILTER_PRESET_ITEM}.IsActive`, true));

    return this.ormDataService.executeQuery(query).pipe(map(rows =>
      (rows as unknown as ReportFilterPresetRecord[]).sort((left, right) =>
        left.ReportFilterPresetOwnershipTypeId - right.ReportFilterPresetOwnershipTypeId
        || left.SortOrder - right.SortOrder
        || left.PresetName.localeCompare(right.PresetName)
      )
    ));
  }

  resolvePreset(record: ReportFilterPresetRecord, now?: Date): ResolvedReportFilterPreset {
    return this.codec.resolve(record, now);
  }

  loadPreference(dashboardKey: string): Observable<ReportFilterPreferenceRecord> {
    const query = new DwQuery(REPORT_FILTER_PREFERENCE_ITEM);
    const filters = new DwQueryHelper().filters;
    query.SetFieldSettings(false, true);
    query.AddFilterAnd(filters.Equal(`${REPORT_FILTER_PREFERENCE_ITEM}.DashboardKey`, dashboardKey));

    return this.ormDataService.executeQuery(query).pipe(map(rows =>
      rows?.length ? rows[0] as unknown as ReportFilterPreferenceRecord : null
    ));
  }

  setDefaultPreset(dashboardKey: string, presetId?: number): Observable<ReportFilterPreferenceRecord> {
    const userId = this.adminManager.getPilexosUser()?.user?.UserId;
    if (!userId) {
      return throwError('An authenticated user is required to save a default report view.');
    }

    return this.loadPreference(dashboardKey).pipe(mergeMap(existing => {
      if (!existing && !presetId) {
        return of(null as ReportFilterPreferenceRecord);
      }

      const values: ReportFilterPreferenceRecord = {
        ...(existing || {} as ReportFilterPreferenceRecord),
        UserId: userId,
        DashboardKey: dashboardKey,
        DefaultReportFilterPresetId: presetId || null
      };
      const entity = existing
        ? Object.assign(existing, values)
        : this.ormDataService.createDummyEntity(REPORT_FILTER_PREFERENCE_ITEM, values);

      return this.ormDataService.saveEntity(entity)
        .pipe(map(saved => saved as unknown as ReportFilterPreferenceRecord));
    }));
  }

  savePreset(
    dashboardKey: string,
    name: string,
    ownershipType: ReportFilterPresetOwnershipType,
    filterGroup: DwQueryFilterGroup,
    filterState?: ReportSavedFilterState,
    existing?: ReportFilterPresetRecord
  ): Observable<ReportFilterPresetRecord> {
    const user = this.adminManager.getPilexosUser();
    const serialized = this.codec.serialize(filterGroup, filterState);
    const values: ReportFilterPresetRecord = {
      ...(existing || {} as ReportFilterPresetRecord),
      DashboardKey: dashboardKey,
      PresetName: name,
      ReportFilterPresetOwnershipTypeId: ownershipType,
      OwnerUserId: ownershipType === ReportFilterPresetOwnershipType.User ? user?.user?.UserId : null,
      FlightSchoolOrganizationId: ownershipType === ReportFilterPresetOwnershipType.FlightSchool
        ? user?.flightSchoolOrganizationId
        : null,
      FilterGroupJSON: serialized.FilterGroupJSON,
      FilterStateJSON: serialized.FilterStateJSON,
      SortOrder: existing?.SortOrder || 0,
      IsActive: true
    };
    const entity = existing
      ? Object.assign(existing, values)
      : this.ormDataService.createDummyEntity(REPORT_FILTER_PRESET_ITEM, values);

    return this.ormDataService.saveEntity(entity).pipe(map(saved => saved as unknown as ReportFilterPresetRecord));
  }

  applyPresetManagement(
    presets: ReportFilterPresetRecord[],
    changes: ReportFilterPresetManageResult
  ): Observable<ReportFilterPresetRecord[]> {
    const orderById = new Map<number, number>();
    (changes.orders || []).forEach(order => orderById.set(order.presetId, order.sortOrder));
    const removedIds = new Set(changes.removedPresetIds || []);
    const updates = (presets || []).filter(preset => {
      const presetId = preset.ReportFilterPresetId;
      return removedIds.has(presetId)
        || (orderById.has(presetId) && preset.SortOrder !== orderById.get(presetId));
    });

    if (!updates.length) {
      return of([]);
    }

    return forkJoin(updates.map(preset => {
      if (removedIds.has(preset.ReportFilterPresetId)) {
        preset.IsActive = false;
      }
      if (orderById.has(preset.ReportFilterPresetId)) {
        preset.SortOrder = orderById.get(preset.ReportFilterPresetId);
      }
      return this.ormDataService.saveEntity(preset)
        .pipe(map(saved => saved as unknown as ReportFilterPresetRecord));
    }));
  }
}
